import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { buildGuidebookUrl } from 'src/shared/util/guidebook-url';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import {
  AddressGuidebookService,
  AddressWithSettings,
  GUIDEBOOK_V2_CATEGORIES,
  ReservationSummary,
  GuidebookV2Category,
  GuidebookV2CategoryId,
} from 'src/shared/services/address-guidebook.service';
import {
  buildGuidebookModel,
  categoryVisibilityUpserts,
  entryUpsert,
  fixedCardEntries,
  GuidebookAudience,
  GuidebookEditorTarget,
  GuidebookEntry,
  GuidebookModel,
  GuidebookPhoto,
  GuidebookSectionEditorParams,
  guestSettingsUpserts,
  sectionOrderUpsert,
  withAudience,
} from 'src/shared/services/guidebook-entries';
import { isRentalAccount } from 'src/shared/services/guidebook-address-fields';
import { ReservationInteractionService } from 'src/pages/schedule/services/reservation-interaction.service';
import { GuidebookCardPhoto, GuidebookPhotosService, groupPhotosByCard, PickedImage } from 'src/shared/services/guidebook-photos.service';
import {
  GuidebookAnswer,
  GuidebookSearchService,
  rankSections,
  SearchableSection,
} from 'src/shared/services/guidebook-search.service';

import { GuidebookSectionEditorComponent } from './guidebook-section-editor/guidebook-section-editor.component';
import { buildDemoGuidebookAddresses } from 'src/shared/constants/demo/guidebook-demo';
import { GuidebookAskBarComponent } from './components/guidebook-ask-bar/guidebook-ask-bar.component';
import { GuidebookDoorCodesComponent } from './components/guidebook-door-codes/guidebook-door-codes.component';
import { SmartlockTestCodeComponent } from 'src/shared/components/smartlock-test-code/smartlock-test-code.component';
import { GuidebookCardPhotosComponent } from './components/guidebook-card-photos/guidebook-card-photos.component';

import { DropdownMenuItem } from 'src/shared/components/dropdown-menu/dropdown-menu.component';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

const LAST_ADDRESS_KEY = 'guidebookAddressId';
const GUEST_SETTINGS_BUSY_KEY = 'guest_settings';
const VIEW_MODE_KEY = 'guidebookViewMode';
const DEMO_PREVIEW_TOKEN = 'test-guidebook';
// Rows longer than this (or multi-line) take a full grid row instead of a column.
const WIDE_ROW_CHARS = 90;
const MAX_LEXICAL_MATCHES = 8;
// Listing names run long enough to swallow the rest of the header row.
const MAX_PROPERTY_NAME_CHARS = 30;

type SectionKind = 'text' | 'wifi' | 'category' | 'contacts' | 'legacy';
type MenuAction = 'edit' | 'move_internal' | 'move_guest' | 'remove';
type ViewMode = 'collapsed' | 'expanded' | 'guest' | 'internal';

const VIEW_MODES: ViewMode[] = ['collapsed', 'expanded', 'guest', 'internal'];

interface SectionRow {
  label: string;
  value: string;
  wide: boolean;
  mono: boolean;
}

/** One card in the list. Mirrors what the public site renders for that section. */
interface SectionVM {
  id: string;
  kind: SectionKind;
  audience: GuidebookAudience;
  title: string;
  icon: string;
  text: string | null;
  rows: SectionRow[];
  photos: GuidebookPhoto[];
  /** Cards whose photos the editor manages (Edit Property photos) don't take guidebook card photos too. */
  takesCardPhotos: boolean;
  /** Fixed cards render even when empty, with a nudge to fill them in. */
  showEmptyHint: boolean;
  categoryId: GuidebookV2CategoryId | null;
  entryKey: string | null;
  draggable: boolean;
  menuItems: DropdownMenuItem[];
  isConfirmingRemove: boolean;
  isBusy: boolean;
  isExpanded: boolean;
}

interface SourceChip {
  id: string;
  title: string;
}

const TEXT_SECTION_ICONS: Record<string, string> = {
  welcome_message: 'assets/svg/happy-outline.svg',
  check_in_instructions: 'assets/svg/key-outline.svg',
  house_rules: 'assets/svg/document-text-outline.svg',
};

const LEGACY_ICON = 'assets/svg/bulb-outline.svg';
const WIFI_ICON = 'assets/svg/wifi-outline.svg';

const EDIT_ITEM: DropdownMenuItem = { value: 'edit', label: 'Edit', icon: 'edit' };
const MOVE_INTERNAL_ITEM: DropdownMenuItem = { value: 'move_internal', label: 'Move to internal', icon: 'lock' };
const MOVE_GUEST_ITEM: DropdownMenuItem = { value: 'move_guest', label: 'Show to guests', icon: 'public' };
const REMOVE_ITEM: DropdownMenuItem = { value: 'remove', label: 'Remove', icon: 'delete' };

function toRow(label: string, value: string, mono = false): SectionRow {
  return { label, value, mono, wide: value.length > WIDE_ROW_CHARS || value.includes('\n') };
}

function truncate(name: string): string {
  return name.length > MAX_PROPERTY_NAME_CHARS ? `${name.slice(0, MAX_PROPERTY_NAME_CHARS).trimEnd()}…` : name;
}

function sectionText(section: SectionVM): string {
  const rows = section.rows.map(row => `${row.label}: ${row.value}`);
  const captions = section.photos.map(photo => photo.caption);
  return [section.text || '', ...rows, ...captions].filter(Boolean).join('\n');
}

function storedViewMode(): ViewMode {
  const stored = localStorage.getItem(VIEW_MODE_KEY) as ViewMode | null;
  return stored && VIEW_MODES.includes(stored) ? stored : 'collapsed';
}

@Component({
  selector: 'app-guidebook',
  templateUrl: './guidebook.page.html',
  styleUrls: ['./guidebook.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
    GuidebookAskBarComponent,
    GuidebookDoorCodesComponent,
    GuidebookCardPhotosComponent,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GuidebookPage implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly urlState = inject(UrlStateService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly demoDataService = inject(DemoDataService);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly addressGuidebookService = inject(AddressGuidebookService);
  private readonly guidebookSearch = inject(GuidebookSearchService);
  private readonly reservationInteraction = inject(ReservationInteractionService);
  private readonly guidebookPhotos = inject(GuidebookPhotosService);
  readonly windowService = inject(WindowService);

  private editorOpen = false;
  private stayOpen = false;
  // Rental accounts' guidebook shows the guest parking / access instructions.
  private readonly isRental = isRentalAccount();
  showAboutThisPage = false;
  /** The demo account gets hard-coded guidebooks; its endpoint is the global addresses path, so no interceptor template. */
  readonly isDemo = this.demoDataService.isDemoAccount();

  readonly addresses = signal<AddressWithSettings[]>([]);
  readonly addressId = signal<number | null>(null);
  readonly model = signal<GuidebookModel | null>(null);
  readonly isLoaded = signal(false);
  readonly errorMessage = signal('');
  readonly isRearranging = signal(false);
  readonly confirmingRemoveKey = signal<string | null>(null);
  readonly busyKeys = signal<ReadonlySet<string>>(new Set());
  // Bumped to re-create the contact form switch from the saved value when a change does not stick.
  readonly contactToggleRev = signal(0);
  readonly previewUuid = signal<string | null>(null);
  /** Bumped when a stay opened from How to Get In closes, so its door code reloads. */
  readonly doorCodesRev = signal(0);
  /** The selected property's guidebook photos, keyed by card id. */
  readonly photosByCard = signal<Record<string, GuidebookCardPhoto[]>>({});
  /** Cards with a photo save running, as `addressId:cardKey`. */
  readonly savingPhotoCards = signal<ReadonlySet<string>>(new Set());

  /** View picker: which cards start open, and which group to show. Per-card clicks override it. */
  readonly viewMode = signal<ViewMode>(storedViewMode());
  private readonly cardOverrides = signal<ReadonlyMap<string, boolean>>(new Map());

  /** Ask bar state. The question filters the list instantly; the answer arrives after. */
  readonly question = signal('');
  readonly isAsking = signal(false);
  readonly answer = signal<GuidebookAnswer | null>(null);
  readonly askError = signal('');
  readonly showAllWhileAsked = signal(false);

  readonly propertyControl = new FormControl<string>('', { nonNullable: true });
  readonly viewControl = new FormControl<ViewMode>(storedViewMode(), { nonNullable: true });

  readonly viewItems: SelectModel[] = [
    { value: 'collapsed', viewValue: 'All collapsed' },
    { value: 'expanded', viewValue: 'All expanded' },
    { value: 'guest', viewValue: 'Guest sections only' },
    { value: 'internal', viewValue: 'Internal notes only' },
  ];

  // Title widths for the loading state, shaped like a typical guidebook's section list.
  readonly skeletonGuestWidths = [170, 200, 120, 90, 140, 125, 150, 110, 190, 220, 105];
  readonly skeletonInternalWidths = [230, 260, 90];

  readonly propertyName = computed(() => this.model()?.propertyName ?? 'Guidebook');
  readonly propertyItems = computed<SelectModel[]>(() =>
    this.addresses().map(address => ({
      value: String(address.id),
      viewValue: truncate(address.address_name || address.address1 || `Property ${address.id}`),
    }))
  );
  readonly showGuestGroup = computed(() => this.viewMode() !== 'internal');
  readonly showInternalGroup = computed(() => this.viewMode() !== 'guest');

  private readonly entries = computed(() => this.model()?.entries ?? []);

  private readonly orderedCategories = computed<GuidebookV2Category[]>(() => {
    const order = this.model()?.categoryOrder ?? [];
    return order
      .map(id => GUIDEBOOK_V2_CATEGORIES.find(category => category.id === id))
      .filter((category): category is GuidebookV2Category => !!category);
  });

  /** Welcome, check-in, standalone WiFi and house rules, in the site's fixed order. */
  readonly fixedSections = computed<SectionVM[]>(() => {
    const model = this.model();
    if (!model) { return []; }
    const sections: SectionVM[] = [];
    const welcome = this.textSection('welcome_message', 'guest');
    if (welcome) { sections.push(welcome); }
    const checkIn = this.textSection('check_in_instructions', 'guest');
    if (checkIn) { sections.push(checkIn); }
    // The site falls back to a standalone WiFi card when the Connectivity section is hidden.
    const network = this.entryValue('wifi_network');
    if (model.categoryVisibility.connectivity === false && network) {
      sections.push({
        ...this.emptySection('wifi', 'wifi', 'guest', 'WiFi', WIFI_ICON),
        rows: this.wifiRows(),
        menuItems: [EDIT_ITEM],
      });
    }
    const rules = this.textSection('house_rules', 'guest');
    if (rules) { sections.push(rules); }
    return sections;
  });

  /** Visible categories with something to show, in the owner's order. Aligned with categorySections. */
  private readonly draggableCategories = computed<GuidebookV2Category[]>(() => {
    const model = this.model();
    if (!model) { return []; }
    return this.orderedCategories().filter(category =>
      model.categoryVisibility[category.id] !== false && !!this.categorySection(category, 'guest')
    );
  });

  readonly categorySections = computed<SectionVM[]>(() =>
    this.draggableCategories()
      .map(category => this.categorySection(category, 'guest'))
      .filter((section): section is SectionVM => !!section)
  );

  readonly legacySections = computed<SectionVM[]>(() =>
    this.entries()
      .filter(entry => entry.kind === 'custom' && entry.audience === 'guest' && entry.categoryId === null && entry.value.trim() !== '')
      .map(entry => ({
        ...this.emptySection(entry.key, 'legacy', 'guest', entry.label, LEGACY_ICON),
        text: entry.value,
        entryKey: entry.key,
        menuItems: [EDIT_ITEM, MOVE_INTERNAL_ITEM, REMOVE_ITEM],
        isConfirmingRemove: this.confirmingRemoveKey() === entry.key,
        isBusy: this.busyKeys().has(entry.key),
      }))
  );

  /** Everything guests can't see: hidden fixed texts, hidden categories, and internal notes. */
  readonly internalSections = computed<SectionVM[]>(() => {
    const model = this.model();
    if (!model) { return []; }
    const sections: SectionVM[] = [];
    for (const key of Object.keys(TEXT_SECTION_ICONS)) {
      const section = this.textSection(key, 'internal');
      if (section) { sections.push(section); }
    }
    for (const category of this.orderedCategories()) {
      if (model.categoryVisibility[category.id] !== false) { continue; }
      const section = this.categorySection(category, 'internal');
      if (section) { sections.push(section); }
    }
    for (const entry of this.entries()) {
      if (entry.kind !== 'custom' || entry.audience !== 'internal') { continue; }
      sections.push({
        ...this.emptySection(entry.key, 'legacy', 'internal', entry.label, LEGACY_ICON),
        text: entry.value,
        entryKey: entry.key,
        menuItems: [EDIT_ITEM, MOVE_GUEST_ITEM, REMOVE_ITEM],
        isConfirmingRemove: this.confirmingRemoveKey() === entry.key,
        isBusy: this.busyKeys().has(entry.key),
      });
    }
    return sections;
  });

  private readonly allSections = computed<SectionVM[]>(() => [
    ...this.fixedSections(),
    ...this.categorySections(),
    ...this.legacySections(),
    ...this.internalSections(),
  ]);

  private readonly searchables = computed<SearchableSection[]>(() =>
    this.allSections().map(section => ({
      id: section.id,
      title: section.title,
      audience: section.audience,
      text: sectionText(section),
    }))
  );

  private readonly lexicalMatches = computed(() => rankSections(this.question(), this.searchables()));

  /** Sections to show while a question is active; null = no filter. */
  private readonly matchIds = computed<ReadonlySet<string> | null>(() => {
    if (!this.question() || this.showAllWhileAsked()) { return null; }
    const ids = new Set<string>();
    const answer = this.answer();
    if (answer?.found) {
      answer.sourceIds.forEach(id => ids.add(id));
    }
    this.lexicalMatches().slice(0, MAX_LEXICAL_MATCHES).forEach(match => ids.add(match.id));
    return ids;
  });

  readonly isFiltering = computed(() => this.matchIds() !== null);
  readonly visibleFixed = computed(() => this.showGuestGroup() ? this.present(this.fixedSections()) : []);
  readonly visibleDraggableCategories = computed(() => {
    if (!this.showGuestGroup()) { return []; }
    const ids = this.matchIds();
    return ids ? this.draggableCategories().filter(category => ids.has(category.id)) : this.draggableCategories();
  });
  readonly visibleCategorySections = computed(() => this.showGuestGroup() ? this.present(this.categorySections()) : []);
  readonly visibleLegacy = computed(() => this.showGuestGroup() ? this.present(this.legacySections()) : []);
  readonly visibleInternal = computed(() => this.showInternalGroup() ? this.present(this.internalSections()) : []);
  readonly hasGuestSections = computed(() =>
    this.fixedSections().length > 0 || this.categorySections().length > 0 || this.legacySections().length > 0
  );
  readonly visibleGuestCount = computed(() =>
    this.visibleFixed().length + this.visibleCategorySections().length + this.visibleLegacy().length
  );
  readonly matchCount = computed(() => this.visibleGuestCount() + this.visibleInternal().length);

  readonly sourceChips = computed<SourceChip[]>(() => {
    const sections = this.allSections();
    return (this.answer()?.sourceIds ?? [])
      .map(id => ({ id, title: sections.find(section => section.id === id)?.title ?? '' }))
      .filter(chip => chip.title !== '');
  });

  async ngOnInit() {
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('guidebook');
    // Desktop edits happen in the right-side panel; refresh once it closes.
    this.rightSidePanelService.afterPanelClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.onEditorClosed());
    await this.loadPage();
  }

  // Mobile edits are a pushed page; this fires when it pops back.
  ionViewWillEnter() {
    if (!this.windowService.isDesktopRes) {
      this.onEditorClosed();
    }
  }

  async loadPage() {
    try {
      const addresses = this.isDemo
        ? buildDemoGuidebookAddresses()
        : await this.addressGuidebookService.listAddressesWithSettings();
      this.addresses.set(addresses);
      const requested = Number(this.urlState.getQueryParam('addressId')) || Number(localStorage.getItem(LAST_ADDRESS_KEY)) || null;
      const picked = addresses.find(address => address.id === requested) ?? addresses[0] ?? null;
      if (picked) {
        this.selectAddress(picked);
      } else {
        this.errorMessage.set('No properties yet. Add a property to start its guidebook.');
      }
    } catch {
      this.errorMessage.set('Failed to load your guidebooks.');
    }
    this.isLoaded.set(true);
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.aboutThisPageService.dismissAboutThisPage('guidebook');
  }

  onPropertyChange(value: string) {
    const address = this.addresses().find(candidate => String(candidate.id) === String(value));
    if (address && address.id !== this.addressId()) {
      this.selectAddress(address);
    }
  }

  // ---- View -------------------------------------------------------------------

  onViewChange(value: string) {
    const mode = VIEW_MODES.includes(value as ViewMode) ? value as ViewMode : 'collapsed';
    this.viewMode.set(mode);
    this.viewControl.setValue(mode, { emitEvent: false });
    localStorage.setItem(VIEW_MODE_KEY, mode);
    this.cardOverrides.set(new Map());
    this.confirmingRemoveKey.set(null);
  }

  toggleCard(section: SectionVM) {
    if (this.isRearranging()) { return; }
    this.cardOverrides.update(current => {
      const next = new Map(current);
      next.set(section.id, !section.isExpanded);
      return next;
    });
  }

  // ---- Ask ------------------------------------------------------------------

  async onAsk(query: string) {
    const question = query.trim();
    if (!question) { return; }
    this.question.set(question);
    this.answer.set(null);
    this.askError.set('');
    this.showAllWhileAsked.set(false);
    this.isRearranging.set(false);
    this.confirmingRemoveKey.set(null);
    this.cardOverrides.set(new Map());
    if (this.isDemo) {
      this.answer.set(this.demoAnswer(question));
      return;
    }
    this.isAsking.set(true);
    try {
      const result = await this.guidebookSearch.answer(question, this.searchables(), this.lexicalMatches());
      // Ignore a late answer if the question changed underneath it.
      if (this.question() === question) {
        this.answer.set(result);
      }
    } catch {
      if (this.question() === question) {
        this.askError.set('TIDY couldn’t write an answer right now. Showing the matching sections instead.');
      }
    } finally {
      if (this.question() === question) {
        this.isAsking.set(false);
      }
    }
  }

  clearAsk() {
    this.question.set('');
    this.answer.set(null);
    this.askError.set('');
    this.isAsking.set(false);
    this.showAllWhileAsked.set(false);
    this.cardOverrides.set(new Map());
  }

  showAllSections() {
    this.showAllWhileAsked.set(true);
  }

  scrollToSection(id: string) {
    this.showAllWhileAsked.set(true);
    this.cardOverrides.update(current => {
      const next = new Map(current);
      next.set(id, true);
      return next;
    });
    setTimeout(() => {
      const element = document.getElementById(`gbd-section-${id}`);
      if (!element) { return; }
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('gbd-card--flash');
      setTimeout(() => element.classList.remove('gbd-card--flash'), 1600);
    });
  }

  // ---- Actions --------------------------------------------------------------

  toggleRearrange() {
    this.isRearranging.update(value => !value);
    this.confirmingRemoveKey.set(null);
    if (this.isRearranging() && this.question()) {
      this.showAllWhileAsked.set(true);
    }
  }

  openStay(reservation: ReservationSummary) {
    if (this.demoBlock.block()) { return; }
    this.stayOpen = true;
    this.reservationInteraction.openDetail(reservation as any, {
      address: this.propertyName(),
      addressId: this.addressId() ?? undefined,
    });
  }

  async openGenerateCode(remoteAccessDeviceId: number) {
    if (this.demoBlock.block()) { return; }
    const params = { preselectedDeviceId: remoteAccessDeviceId };
    if (this.windowService.isDesktopRes) {
      await this.rightSidePanelService.navigateTo('smartlock-test-code', params, SmartlockTestCodeComponent);
    } else {
      await this.storage.save('dialog-params', params);
      this.navCtrl.navigateForward('smartlock-test-code', params);
    }
  }

  // preview=1 has the site show a sample door code when the stay has none yet.
  openPreview() {
    this.util.openUrl(`${buildGuidebookUrl(this.previewUuid() || DEMO_PREVIEW_TOKEN)}?preview=1`);
  }

  // The editor asks which guidebook the new section belongs to, so one entry point covers both.
  addSection() {
    void this.openEditor({ type: 'new' }, 'guest');
  }

  onMenuSelect(section: SectionVM, action: MenuAction) {
    switch (action) {
      case 'edit':
        void this.editSection(section);
        break;
      case 'move_internal':
        void this.moveSection(section, 'internal');
        break;
      case 'move_guest':
        void this.moveSection(section, 'guest');
        break;
      case 'remove':
        this.confirmingRemoveKey.set(section.id);
        this.cardOverrides.update(current => {
          const next = new Map(current);
          next.set(section.id, true);
          return next;
        });
        break;
    }
  }

  cancelRemove() {
    this.confirmingRemoveKey.set(null);
  }

  async confirmRemove(section: SectionVM) {
    const addressId = this.addressId();
    if (!section.entryKey || !addressId || this.demoBlock.block()) { return; }
    const key = section.entryKey;
    this.setBusy(key, true);
    try {
      await this.addressGuidebookService.deleteGuidebookSettings(addressId, [key]);
      this.updateModel(model => ({ ...model, entries: model.entries.filter(entry => entry.key !== key) }));
      this.confirmingRemoveKey.set(null);
      this.util.showSuccess('Section removed', 2000);
      void this.deleteCardPhotos(addressId, key);
    } catch {
      this.util.showError('Failed to remove. Please try again.', 3000);
    } finally {
      this.setBusy(key, false);
    }
  }

  // Only visible categories are draggable; hidden or empty ones keep their slot in the stored order.
  async onSectionsReordered(reordered: GuidebookV2Category[]) {
    const model = this.model();
    const addressId = this.addressId();
    if (!model || !addressId) { return; }
    const draggableIds = new Set(this.visibleDraggableCategories().map(category => category.id));
    const queue = reordered.map(category => category.id);
    const order = model.categoryOrder.map(id => draggableIds.has(id) ? queue.shift() ?? id : id);
    this.updateModel(current => ({ ...current, categoryOrder: order }));
    try {
      await this.addressGuidebookService.upsertGuidebook(addressId, [sectionOrderUpsert(order)]);
    } catch {
      this.util.showError('Failed to save the section order.', 3000);
    }
  }

  // ---- Internals --------------------------------------------------------------

  private selectAddress(address: AddressWithSettings) {
    this.addressId.set(address.id);
    localStorage.setItem(LAST_ADDRESS_KEY, String(address.id));
    this.urlState.updateQueryParams({ addressId: address.id });
    this.propertyControl.setValue(String(address.id), { emitEvent: false });
    this.model.set(buildGuidebookModel(address, this.isRental));
    this.errorMessage.set('');
    this.clearAsk();
    this.isRearranging.set(false);
    this.confirmingRemoveKey.set(null);
    void this.loadPreviewUuid();
    void this.loadPhotos();
  }

  // The address and card are fixed when the save starts: the host can switch property
  // or close the card before it ends, and each photo still lands where it was added.
  async addCardPhotos(cardKey: string, picked: PickedImage[]) {
    const addressId = this.addressId();
    const savingKey = `${addressId}:${cardKey}`;
    if (!addressId || !picked?.length || this.savingPhotoCards().has(savingKey) || this.demoBlock.block()) { return; }
    this.setPhotoSaving(savingKey, true);
    let saved = 0;
    try {
      for (const image of picked) {
        const photo = await this.guidebookPhotos.addPhoto(addressId, cardKey, image);
        saved++;
        this.applyCardPhotos(addressId, cardKey, photos =>
          photos.some(existing => existing.id === photo.id) ? photos : [...photos, photo]);
      }
    } catch {
      this.util.showError(saved
        ? 'Some photos could not be added. Please try again.'
        : 'Could not add the photo. Please try again.');
    } finally {
      this.setPhotoSaving(savingKey, false);
    }
  }

  async removeCardPhoto(cardKey: string, photoId: number) {
    const addressId = this.addressId();
    const savingKey = `${addressId}:${cardKey}`;
    if (!addressId || this.savingPhotoCards().has(savingKey) || this.demoBlock.block()) { return; }
    this.setPhotoSaving(savingKey, true);
    try {
      await this.guidebookPhotos.deletePhoto(addressId, photoId);
      this.applyCardPhotos(addressId, cardKey, photos => photos.filter(photo => photo.id !== photoId));
    } catch {
      this.util.showError('Could not delete the photo. Please try again.');
    } finally {
      this.setPhotoSaving(savingKey, false);
    }
  }

  // A removed custom card's key comes back if a card with the same name is added,
  // so its photos go with it. Best effort: the card is already gone for the host.
  private async deleteCardPhotos(addressId: number, cardKey: string) {
    let photos = this.photosByCard()[cardKey] || [];
    if (this.addressId() !== addressId) {
      try {
        photos = (await this.guidebookPhotos.listPhotos(addressId)).filter(photo => photo.card_key === cardKey);
      } catch {
        return;
      }
    }
    const results = await Promise.allSettled(photos.map(photo => this.guidebookPhotos.deletePhoto(addressId, photo.id)));
    const deleted = new Set(photos.filter((_, i) => results[i].status === 'fulfilled').map(photo => photo.id));
    this.applyCardPhotos(addressId, cardKey, current => current.filter(photo => !deleted.has(photo.id)));
  }

  private applyCardPhotos(addressId: number, cardKey: string, change: (photos: GuidebookCardPhoto[]) => GuidebookCardPhoto[]) {
    if (this.addressId() !== addressId) { return; }
    this.photosByCard.update(byCard => ({ ...byCard, [cardKey]: change(byCard[cardKey] || []) }));
  }

  private setPhotoSaving(savingKey: string, saving: boolean) {
    this.savingPhotoCards.update(current => {
      const next = new Set(current);
      if (saving) { next.add(savingKey); } else { next.delete(savingKey); }
      return next;
    });
  }

  // A failed load leaves the cards without photos rather than blocking the guidebook.
  private async loadPhotos() {
    this.photosByCard.set({});
    const addressId = this.addressId();
    if (!addressId || this.isDemo) { return; }
    try {
      const photos = await this.guidebookPhotos.listPhotos(addressId);
      if (this.addressId() !== addressId) { return; }
      this.photosByCard.set(groupPhotosByCard(photos));
    } catch {
      this.photosByCard.set({});
    }
  }

  // Applies the search filter and resolves each card's open/closed state.
  private present(sections: SectionVM[]): SectionVM[] {
    const ids = this.matchIds();
    const filtered = ids ? sections.filter(section => ids.has(section.id)) : sections;
    return filtered.map(section => ({ ...section, isExpanded: this.isExpanded(section, ids !== null) }));
  }

  // Rearranging collapses everything; search matches open; otherwise the view picker
  // decides, and a click on the card wins over all of that.
  private isExpanded(section: SectionVM, isMatch: boolean): boolean {
    if (this.isRearranging()) { return false; }
    const override = this.cardOverrides().get(section.id);
    if (override !== undefined) { return override; }
    if (isMatch) { return true; }
    switch (this.viewMode()) {
      case 'expanded':
      case 'guest':
      case 'internal':
        return true;
      case 'collapsed':
      default:
        return false;
    }
  }

  private async editSection(section: SectionVM) {
    switch (section.kind) {
      case 'text':
        await this.openEditor({ type: 'text', key: section.entryKey! }, section.audience);
        break;
      case 'wifi':
        await this.openEditor({ type: 'category', categoryId: 'connectivity' }, section.audience);
        break;
      case 'category':
        await this.openEditor({ type: 'category', categoryId: section.categoryId! }, section.audience);
        break;
      case 'legacy':
        await this.openEditor({ type: 'custom', key: section.entryKey! }, section.audience);
        break;
      case 'contacts':
        break;
    }
  }

  private async openEditor(target: GuidebookEditorTarget, audience: GuidebookAudience) {
    const addressId = this.addressId();
    if (this.demoBlock.block() || !addressId) { return; }
    const params: GuidebookSectionEditorParams = { addressId, audience, target };
    await this.storage.save('dialog-params', params);
    this.editorOpen = true;
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(GuidebookSectionEditorComponent);
    } else {
      await this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward('guidebook-section-editor', params);
    }
  }

  private onEditorClosed() {
    if (this.stayOpen) {
      // A code added on the stay shows up in How to Get In's list.
      this.stayOpen = false;
      this.doorCodesRev.update(rev => rev + 1);
    }
    if (!this.editorOpen) { return; }
    this.editorOpen = false;
    void this.refresh();
  }

  private async refresh() {
    const addressId = this.addressId();
    if (!addressId) { return; }
    try {
      const address = await this.addressGuidebookService.getAddressWithSettings(addressId);
      this.addresses.update(list => list.map(candidate => candidate.id === addressId ? address : candidate));
      this.model.set(buildGuidebookModel(address, this.isRental));
      this.errorMessage.set('');
      // The editor can hand over to a stay, where its code may have changed.
      this.doorCodesRev.update(rev => rev + 1);
    } catch {
      this.util.showError('Failed to reload this guidebook.', 3000);
    }
  }

  // The demo account has no LLM access (its POSTs are blocked), so the answer is the ranker's top hits.
  private demoAnswer(question: string): GuidebookAnswer {
    const sections = this.allSections();
    const sourceIds = this.lexicalMatches().slice(0, 3).map(match => match.id);
    const titles = sourceIds.map(id => sections.find(section => section.id === id)?.title).filter(Boolean);
    return {
      text: titles.length
        ? `Here’s what this guidebook says about “${question}”. The most relevant sections are ${titles.join(', ')}; open them below for the details.`
        : `This guidebook doesn’t cover “${question}” yet. Add a section or an internal note so the team can find it next time.`,
      sourceIds,
      found: sourceIds.length > 0,
    };
  }

  // The public site renders per reservation, so Preview borrows the next stay's link.
  private async loadPreviewUuid() {
    const addressId = this.addressId();
    if (!addressId || this.isDemo) { return; }
    try {
      const reservations = await this.addressGuidebookService.listReservationsForAddress(addressId, 'active');
      if (this.addressId() !== addressId) { return; }
      const today = new Date().toISOString().slice(0, 10);
      const withUuid = reservations.filter(reservation => !!reservation.uuid);
      const upcoming = withUuid
        .filter(reservation => (reservation.check_in_date || '') >= today)
        .sort((a, b) => (a.check_in_date || '').localeCompare(b.check_in_date || ''));
      const latest = [...withUuid].sort((a, b) => (b.check_in_date || '').localeCompare(a.check_in_date || ''));
      this.previewUuid.set((upcoming[0] || latest[0])?.uuid ?? null);
    } catch {
      this.previewUuid.set(null);
    }
  }

  private async moveSection(section: SectionVM, audience: GuidebookAudience) {
    if (section.kind === 'category' || section.kind === 'contacts') {
      await this.setCategoryVisible(section.categoryId!, audience === 'guest');
      return;
    }
    const entry = this.entries().find(candidate => candidate.key === section.entryKey);
    if (!entry) { return; }
    const updated = withAudience(entry, audience);
    const saved = await this.persistEntry(updated);
    if (saved) {
      this.util.showSuccess(
        audience === 'guest'
          ? `“${section.title}” is now shown to guests`
          : `“${section.title}” is now internal only`,
        3000
      );
    }
  }

  private async setCategoryVisible(categoryId: GuidebookV2CategoryId, visible: boolean) {
    const model = this.model();
    const addressId = this.addressId();
    if (!model || !addressId || this.demoBlock.block()) { return; }
    const label = GUIDEBOOK_V2_CATEGORIES.find(category => category.id === categoryId)?.label ?? 'Section';
    this.setBusy(categoryId, true);
    try {
      await this.addressGuidebookService.upsertGuidebook(
        addressId,
        categoryVisibilityUpserts(categoryId, visible, model.entries)
      );
      this.updateModel(current => ({
        ...current,
        categoryVisibility: { ...current.categoryVisibility, [categoryId]: visible },
      }));
      this.util.showSuccess(
        visible ? `${label} is now shown to guests` : `${label} is now internal only`,
        3000
      );
    } catch {
      this.util.showError('Failed to update the section. Please try again.', 3000);
    } finally {
      this.setBusy(categoryId, false);
    }
  }

  // Whether the property's guest guidebook asks guests for their contact details.
  async setContactCollection(enabled: boolean) {
    const model = this.model();
    const addressId = this.addressId();
    if (!model || !addressId) { return; }
    if (this.demoBlock.block()) {
      this.contactToggleRev.update(rev => rev + 1);
      return;
    }
    this.setBusy(GUEST_SETTINGS_BUSY_KEY, true);
    try {
      await this.addressGuidebookService.upsertGuidebook(
        addressId,
        guestSettingsUpserts(enabled, model.identityVerificationEnabled)
      );
      if (this.addressId() === addressId) {
        this.updateModel(current => ({ ...current, contactCollectionEnabled: enabled }));
      }
      this.util.showSuccess(enabled ? 'Guests will see the contact form' : 'Contact form hidden from guests', 3000);
    } catch {
      this.contactToggleRev.update(rev => rev + 1);
      this.util.showError('Failed to update the setting. Please try again.', 3000);
    } finally {
      this.setBusy(GUEST_SETTINGS_BUSY_KEY, false);
    }
  }

  private async persistEntry(entry: GuidebookEntry): Promise<boolean> {
    const model = this.model();
    const addressId = this.addressId();
    if (!model || !addressId || this.demoBlock.block()) { return false; }
    const row = entryUpsert(entry, model.categoryVisibility);
    if (!row) { return false; }
    this.setBusy(entry.key, true);
    try {
      await this.addressGuidebookService.upsertGuidebook(addressId, [row]);
      this.updateModel(current => ({
        ...current,
        entries: current.entries.map(existing => existing.key === entry.key ? entry : existing),
      }));
      return true;
    } catch {
      this.util.showError('Failed to save. Please try again.', 3000);
      return false;
    } finally {
      this.setBusy(entry.key, false);
    }
  }

  private updateModel(update: (model: GuidebookModel) => GuidebookModel) {
    const model = this.model();
    if (model) {
      this.model.set(update(model));
    }
  }

  private setBusy(key: string, busy: boolean) {
    this.busyKeys.update(current => {
      const next = new Set(current);
      if (busy) { next.add(key); } else { next.delete(key); }
      return next;
    });
  }

  private entryValue(key: string): string {
    return this.entries().find(entry => entry.key === key)?.value.trim() ?? '';
  }

  private wifiRows(): SectionRow[] {
    return [
      toRow('WiFi network', this.entryValue('wifi_network')),
      toRow('WiFi password', this.entryValue('wifi_password'), true),
    ];
  }

  private emptySection(id: string, kind: SectionKind, audience: GuidebookAudience, title: string, icon: string): SectionVM {
    return {
      id,
      kind,
      audience,
      title,
      icon,
      text: null,
      rows: [],
      photos: [],
      takesCardPhotos: true,
      showEmptyHint: false,
      categoryId: null,
      entryKey: null,
      draggable: false,
      menuItems: [],
      isConfirmingRemove: false,
      isBusy: false,
      isExpanded: false,
    };
  }

  private textSection(key: string, audience: GuidebookAudience): SectionVM | null {
    const entry = this.entries().find(candidate => candidate.key === key);
    if (!entry || entry.audience !== audience || entry.value.trim() === '') { return null; }
    return {
      ...this.emptySection(key, 'text', audience, entry.label, TEXT_SECTION_ICONS[key]),
      text: entry.value,
      entryKey: key,
      menuItems: audience === 'guest' ? [EDIT_ITEM, MOVE_INTERNAL_ITEM] : [EDIT_ITEM, MOVE_GUEST_ITEM],
      isBusy: this.busyKeys().has(key),
    };
  }

  // Mirrors the site's category card: property-record extras first, then the filled
  // fields and the owner's own rows. Returns null when the site would render nothing,
  // except for the fixed cards, which always render so they can be filled in.
  private categorySection(category: GuidebookV2Category, audience: GuidebookAudience): SectionVM | null {
    const entries = this.entries();
    const rows: SectionRow[] = fixedCardEntries(category.id, entries).map(entry => toRow(entry.label, entry.value));
    const photos = this.model()?.photos[category.id] ?? [];
    let text: string | null = null;

    if (category.id === 'connectivity' && this.entryValue('wifi_network')) {
      rows.push(...this.wifiRows());
    }
    if (category.id === 'checkout') {
      const checkout = this.entryValue('check_out_instructions');
      if (checkout) { text = checkout; }
    }
    for (const entry of entries) {
      if (entry.categoryId !== category.id || entry.kind === 'address' || entry.key === 'check_out_instructions') { continue; }
      // A row hidden on its own is an internal note, not part of this card.
      if (entry.kind === 'custom' && entry.audience !== 'guest') { continue; }
      if (entry.value.trim() === '') { continue; }
      rows.push(toRow(entry.label, entry.value));
    }

    const isContacts = category.id === 'contacts';
    const takesCardPhotos = !category.photoKind;
    // The guest site shows a card that only has photos, so the host must see it too.
    const cardPhotos = takesCardPhotos ? this.photosByCard()[category.id] ?? [] : [];
    const isEmpty = rows.length === 0 && !text && photos.length === 0 && cardPhotos.length === 0;
    if (!isContacts && !category.fixed && isEmpty) { return null; }

    let menuItems = [EDIT_ITEM, audience === 'guest' ? MOVE_INTERNAL_ITEM : MOVE_GUEST_ITEM];
    if (isContacts) {
      menuItems = [audience === 'guest' ? MOVE_INTERNAL_ITEM : MOVE_GUEST_ITEM];
    } else if (category.fixed) {
      menuItems = [EDIT_ITEM];
    }

    return {
      ...this.emptySection(category.id, isContacts ? 'contacts' : 'category', audience, category.label, category.icon),
      text,
      rows,
      photos,
      takesCardPhotos,
      showEmptyHint: !!category.fixed && isEmpty,
      categoryId: category.id,
      draggable: audience === 'guest',
      menuItems,
      isBusy: this.busyKeys().has(category.id),
    };
  }
}
